import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import dayjs from "dayjs";

Chart.register(...registerables);

const RevenueChart = ({ data, loading, filterState }) => {
  const lang = localStorage.getItem("language") || "ar";

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [activeTab, setActiveTab] = useState("users");

  // Helper function to calculate date range in days
  const getDateRangeDays = () => {
    if (!filterState?.startDate || !filterState?.endDate) {
      return 30; // Default to 30 days if no dates provided
    }
    const start = dayjs(filterState.startDate);
    const end = dayjs(filterState.endDate);
    return end.diff(start, "day");
  };

  // Helper function to aggregate data by week
  const aggregateByWeek = (dailyData, isRevenue = false) => {
    const grouped = {};

    dailyData.forEach((item) => {
      const date = dayjs(item.date);
      const weekStart = date.startOf("week");
      const weekEnd = date.endOf("week");
      const weekKey = weekStart.format("YYYY-MM-DD");

      if (!grouped[weekKey]) {
        grouped[weekKey] = {
          date: weekKey,
          endDate: weekEnd.format("YYYY-MM-DD"),
          total_users: 0,
          paid_users: 0,
          total_revenue: 0,
        };
      }

      // For revenue, sum all daily values
      // For users, sum all daily values (these are daily new users/paid users, not cumulative)
      if (isRevenue) {
        grouped[weekKey].total_revenue += item.total_revenue || 0;
      } else {
        // Sum all daily values for users
        grouped[weekKey].total_users += item.total_users || 0;
        grouped[weekKey].paid_users += item.paid_users || 0;
      }
    });

    return Object.values(grouped)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => {
        const startDate = dayjs(item.date);
        const endDate = dayjs(item.endDate);
        const label = `${startDate.format("MMM DD")} - ${endDate.format(
          "MMM DD"
        )}`;
        return {
          date: item.date,
          total_users: item.total_users,
          paid_users: item.paid_users,
          total_revenue: item.total_revenue,
          label,
        };
      });
  };

  // Helper function to aggregate data by month
  const aggregateByMonth = (dailyData, isRevenue = false) => {
    const grouped = {};

    dailyData.forEach((item) => {
      const date = dayjs(item.date);
      const monthKey = date.format("YYYY-MM");

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          date: `${monthKey}-01`,
          total_users: 0,
          paid_users: 0,
          total_revenue: 0,
        };
      }

      // For revenue, sum all daily values
      // For users, sum all daily values (these are daily new users/paid users, not cumulative)
      if (isRevenue) {
        grouped[monthKey].total_revenue += item.total_revenue || 0;
      } else {
        // Sum all daily values for users
        grouped[monthKey].total_users += item.total_users || 0;
        grouped[monthKey].paid_users += item.paid_users || 0;
      }
    });

    return Object.values(grouped)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        date: item.date,
        total_users: item.total_users,
        paid_users: item.paid_users,
        total_revenue: item.total_revenue,
        label: dayjs(item.date).format("MMM YYYY"),
      }));
  };

  const chartData = React.useMemo(() => {
    if (!data?.users_daily || !data?.revenue_daily) {
      return {
        labels: [],
        users: [],
        paidUsers: [],
        revenue: [],
        cumulativeRevenue: [],
      };
    }

    const dateRangeDays = getDateRangeDays();
    let processedUsersData = [];
    let processedRevenueData = [];

    // Determine aggregation based on date range
    if (dateRangeDays > 90) {
      // Aggregate by month for more than 90 days (3+ months)
      // For users and revenue, sum all daily values
      processedUsersData = aggregateByMonth(data.users_daily, false);
      processedRevenueData = aggregateByMonth(data.revenue_daily, true);
    } else if (dateRangeDays > 30 && dateRangeDays <= 90) {
      // Aggregate by week for greater than 30 days and up to 90 days
      // For users and revenue, sum all daily values
      processedUsersData = aggregateByWeek(data.users_daily, false);
      processedRevenueData = aggregateByWeek(data.revenue_daily, true);
    } else {
      // Show daily data for 30 days or less (including exactly 30 days)
      // Show all days with day name format
      processedUsersData = data.users_daily.map((item) => ({
        ...item,
        label: `${item.day}-${new Date(item.date).getDate()}`,
      }));
      processedRevenueData = data.revenue_daily.map((item) => ({
        ...item,
        label: `${item.day}-${new Date(item.date).getDate()}`,
      }));
    }

    // Merge users and revenue data by date for consistent labels
    const dateMap = new Map();

    processedUsersData.forEach((item) => {
      const dateKey = dayjs(item.date).format("YYYY-MM-DD");
      dateMap.set(dateKey, {
        date: dateKey,
        label: item.label,
        total_users: item.total_users || 0,
        paid_users: item.paid_users || 0,
        total_revenue: 0,
      });
    });

    processedRevenueData.forEach((item) => {
      const dateKey = dayjs(item.date).format("YYYY-MM-DD");
      if (dateMap.has(dateKey)) {
        dateMap.get(dateKey).total_revenue = item.total_revenue || 0;
      } else {
        dateMap.set(dateKey, {
          date: dateKey,
          label: item.label,
          total_users: 0,
          paid_users: 0,
          total_revenue: item.total_revenue || 0,
        });
      }
    });

    const mergedData = Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const labels = mergedData.map((item) => item.label);
    const users = mergedData.map((item) => item.total_users);
    const paidUsers = mergedData.map((item) => item.paid_users);
    const revenue = mergedData.map((item) => item.total_revenue);

    let running = 0;
    const cumulativeRevenue = mergedData.map((item) => {
      running += item.total_revenue || 0;
      return running;
    });

    return { labels, users, paidUsers, revenue, cumulativeRevenue };
  }, [data, filterState]);

  useEffect(() => {
    if (!chartData.labels.length || !chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const ctx = chartRef.current.getContext("2d");

    // ✅ Plugin: show message if all values are zero
    const noDataTextPlugin = {
      id: "noDataText",
      afterDraw(chart) {
        const isAllZero =
          activeTab === "users"
            ? chartData.users.every((v) => v === 0) &&
              chartData.paidUsers.every((v) => v === 0)
            : chartData.revenue.every((v) => v === 0);

        if (!isAllZero) return;

        const { ctx, chartArea } = chart;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;

        ctx.save();
        ctx.fillStyle = "#9CA3AF"; // gray
        ctx.font = "bold 16px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const message =
          activeTab === "users"
            ? lang === "ar"
              ? "لا توجد أي نشاطات للمستخدم في هذه الفترة الزمنية"
              : "No user activity for this time period"
            : lang === "ar"
            ? "لم يتم تحقيق أي إيرادات خلال هذه الفترة الزمنية"
            : "No revenue generated for this time period";
        ctx.fillText(message, cx, cy);
        ctx.restore();
      },
    };

    // ✅ USERS TAB CHART
    // ✅ USERS TAB CHART
    if (activeTab === "users") {
      const gradientOrange = ctx.createLinearGradient(0, 0, 0, 400);
      gradientOrange.addColorStop(0, "rgba(255, 138, 76, 0.6)");
      gradientOrange.addColorStop(1, "rgba(255, 138, 76, 0.05)");

      const gradientBlue = ctx.createLinearGradient(0, 0, 0, 400);
      gradientBlue.addColorStop(0, "rgba(100, 120, 200, 0.5)");
      gradientBlue.addColorStop(1, "rgba(100, 120, 200, 0.05)");

      const maxUserValue = Math.max(
        ...chartData.users,
        ...chartData.paidUsers,
        0
      );

      chartInstance.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: chartData.labels,
          datasets: [
            {
              label: lang === "ar" ? "إجمالي المستخدمين" : "Total Users",
              data: chartData.users,
              borderColor: "#FF8A4C",
              backgroundColor: gradientOrange,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 0,
            },
            {
              label: lang === "ar" ? "المستخدمون المشتركين" : "Paid Users",
              data: chartData.paidUsers,
              borderColor: "#6478C8",
              backgroundColor: gradientBlue,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 0,
            },
          ],
        },
        plugins: [noDataTextPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                color: "#6B7280",
                font: {
                  size: 12,
                },
              },
            },
            y: {
              beginAtZero: true,
              max: Math.max(maxUserValue + 5, 10),
              grid: {
                color: "#F3F4F6",
              },
              ticks: {
                color: "#6B7280",
                font: {
                  size: 12,
                },
              },
            },
          },
        },
      });
    }

    // ✅ REVENUE TAB CHART
    if (activeTab === "revenue") {
      // Filter out zero values visually but keep all labels
      const displayData = chartData.revenue.map((v) => (v === 0 ? null : v));
      const maxRevenue = Math.max(...chartData.revenue, 0);
      const maxCumulative = Math.max(...chartData.cumulativeRevenue, 0);
      const yMax =
        maxCumulative > 0
          ? maxCumulative + Math.max(maxCumulative * 0.04, 200)
          : 10;
      const revenueBlue = "#6D4AE2";

      const topLabelsPlugin = {
        id: "topLabels",
        afterDatasetsDraw(chart) {
          const { ctx } = chart;
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (dataset.type === "line") return;
            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar, index) => {
              const value = dataset.data[index];
              if (value === null || value === undefined) return;
              ctx.save();
              ctx.font = "600 13px system-ui, sans-serif";
              ctx.fillStyle = "#111827";
              ctx.textAlign = "center";
              ctx.textBaseline = "bottom";
              ctx.fillText(value.toFixed(0), bar.x, bar.y - 8);
              ctx.restore();
            });
          });
        },
      };

      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: chartData.labels,
          datasets: [
            {
              type: "bar",
              label:
                lang === "ar" ? "الإيرادات اليومية" : "Daily Revenue",
              data: displayData, // 👈 null where value = 0
              backgroundColor: displayData.map((v) =>
                v === null ? "transparent" : revenueBlue
              ), // 👈 hides bar for 0
              borderRadius: {
                topLeft: 10,
                topRight: 10,
                bottomLeft: 0,
                bottomRight: 0,
              },
              barThickness: 40,
              borderSkipped: false,
              order: 0,
            },
            {
              type: "line",
              label:
                lang === "ar"
                  ? "الإيرادات التراكمية"
                  : "Cumulative Revenue",
              data: chartData.cumulativeRevenue,
              borderColor: revenueBlue,
              backgroundColor: "transparent",
              borderWidth: 2,
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: revenueBlue,
              pointBorderColor: revenueBlue,
              fill: false,
              order: 1,
            },
          ],
        },
        plugins: [topLabelsPlugin, noDataTextPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          layout: {
            padding: { top: 20, bottom: 0, right: 20 },
          },
          scales: {
            x: {
              offset: true,
              grid: { display: false },
              ticks: {
                color: "#6B7280",
                font: { size: 12 },
              },
            },
            y: {
              beginAtZero: true,
              max: Math.max(yMax, 10),
              grid: {
                drawBorder: false,
                color: "transparent",
              },
              ticks: {
                color: "#6B7280",
                font: { size: 12 },
              },
            },
          },
        },
      });
    }

    // ✅ Cleanup
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartData, activeTab]);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "24px 32px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "28px",
        }}
      >
        <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#111827" }}>
          {activeTab === "users"
            ? lang === "ar"
              ? "اتجاه نمو المستخدمين"
              : "User Growth Trend"
            : lang === "ar"
            ? "الإيرادات بمرور الوقت"
            : "Revenue Over Time"}
        </h2>

        <div className="chart-toggle-group">
          <button
            className={`chart-toggle-btn ${
              activeTab === "users" ? "active" : ""
            }`}
            onClick={() => setActiveTab("users")}
          >
            <img
              src="/dashboard/user-orang.svg"
              alt="Users"
              className="toggle-icon"
            />
            <span>{lang === "ar" ? "المستخدمين" : "Users"}</span>
          </button>

          <button
            className={`chart-toggle-btn ${
              activeTab === "revenue" ? "active revenue" : ""
            }`}
            onClick={() => setActiveTab("revenue")}
          >
            <img
              src={
                activeTab === "revenue"
                  ? "/dashboard/dollor-orange.svg"
                  : "/dashboard/dollor-v2.svg"
              }
              alt="Revenue"
              className="toggle-icon"
            />
            <span>{lang === "ar" ? "الإيرادات" : "Revenue"}</span>
          </button>
        </div>
      </div>

      <div style={{ height: "420px", position: "relative" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#6B7280", paddingTop: 100 }}>
            Loading chart data...
          </p>
        ) : !chartData.labels.length ? (
          <p style={{ textAlign: "center", color: "#6B7280", paddingTop: 100 }}>
            No data available
          </p>
        ) : (
          <canvas ref={chartRef}></canvas>
        )}
      </div>
    </div>
  );
};

export default RevenueChart;
